import { I18n } from '@affine/i18n';
import {
  FontLoaderService,
  isCanvasHandwritingFontReady,
  ThemeProvider,
  waitForCanvasHandwritingFont,
} from '@blocksuite/affine-shared/services';
import { BlockComponent, toGfxBlockComponent } from '@blocksuite/std';
import { GfxViewInteractionExtension } from '@blocksuite/std/gfx';
import { css, html, nothing } from 'lit';
import { state } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';

import {
  type BrushHeadingBlockModel,
  BrushHeadingBlockSchema,
} from '../schemas/brush-heading-model.js';
import {
  brushHeadingTextStyle,
  renderBrushHeadingVisual,
  resolveBrushHeadingColors,
} from './brush-heading-visual.js';

export const AFFINE_BRUSH_HEADING_CANVAS = 'affine-brush-heading-canvas';

export class BrushHeadingBlockComponent extends BlockComponent<BrushHeadingBlockModel> {
  override renderBlock() {
    return nothing;
  }
}

export class BrushHeadingCanvasBlockComponent extends toGfxBlockComponent(
  BrushHeadingBlockComponent
) {
  static override styles = css`
    :host {
      pointer-events: all;
    }
    .heading-shell {
      position: relative;
      width: 100%;
      height: 100%;
      filter: drop-shadow(1px 3px 3px rgb(0 0 0 / 14%));
    }
    .brush-heading-visual {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      overflow: visible;
    }
    .brush-heading-editor {
      position: absolute;
      inset: 0 6%;
      z-index: 1;
      box-sizing: border-box;
      width: 88%;
      height: 100%;
      margin: 0;
      padding: 0;
      overflow: hidden;
      appearance: none;
      border: 0;
      border-radius: 0;
      box-shadow: none;
      outline: 0;
      background: transparent;
      caret-color: var(--heading-fg);
      color: transparent;
      -webkit-text-fill-color: transparent;
    }
    .heading-shell[data-font-ready='false'] .brush-heading-label,
    .heading-shell[data-font-ready='false'] .brush-heading-editor {
      visibility: hidden;
    }
  `;

  @state() private accessor _editing = false;
  @state() private accessor _fontReady = isCanvasHandwritingFontReady();

  override connectedCallback() {
    super.connectedCallback();
    const selection = this.gfx.selection;
    const syncSelection = () => {
      this._editing = selection.has(this.model.id) && selection.editing;
    };
    syncSelection();
    this.disposables.add(selection.slots.updated.subscribe(syncSelection));
    void waitForCanvasHandwritingFont(
      this.std.getOptional(FontLoaderService)
    ).then(
      () => {
        if (this.isConnected) this._fontReady = true;
      },
      () => {
        if (this.isConnected) this._fontReady = true;
      }
    );
  }

  private _enterEditing(event: Event) {
    event.stopPropagation();
    this.gfx.selection.set({ elements: [this.model.id], editing: true });
  }

  private _stopTextInputHotkeys(event: KeyboardEvent) {
    event.stopImmediatePropagation();
  }

  override renderGfxBlock() {
    const theme = this.std.get(ThemeProvider).edgeless$.value;
    const palette = resolveBrushHeadingColors(
      this.model.props.color,
      this.model.props.textColor,
      theme
    );
    const maskId = `brush-heading-${this.model.id.replace(/[^a-zA-Z0-9_-]/g, '-')}`;
    const text = this.model.props.text.trim();
    const placeholder = I18n.t(
      'com.streamlain.canvas.brush-heading.placeholder'
    );
    return html`<div
      class="heading-shell"
      style=${styleMap({
        '--heading-fg': palette.foreground,
        transform: `rotate(${this.model.rotate}deg)`,
        transformOrigin: 'center',
      })}
      data-editing=${this._editing}
      data-font-ready=${this._fontReady}
    >
      ${renderBrushHeadingVisual({
        background: palette.background,
        foreground: palette.foreground,
        fontSize: this.model.props.fontSize,
        height: '100%',
        maskId,
        placeholder: !text,
        text: text || placeholder,
        width: '100%',
      })}
      ${this._editing
        ? html`<input
            class="brush-heading-editor"
            aria-label=${I18n.t(
              'com.streamlain.canvas.brush-heading.text-label'
            )}
            style=${styleMap({
              ...brushHeadingTextStyle(
                palette.foreground,
                false,
                this.model.props.fontSize
              ),
              caretColor: palette.foreground,
              color: 'transparent',
              display: 'block',
              height: '100%',
              WebkitTextFillColor: 'transparent',
              width: '88%',
              zIndex: '1',
            })}
            .value=${this.model.props.text}
            @input=${(event: InputEvent) =>
              this.store.updateBlock(this.model, {
                text: (event.target as HTMLInputElement).value,
              })}
            @pointerdown=${(event: Event) => event.stopPropagation()}
            @focus=${this._enterEditing}
            @keydown=${this._stopTextInputHotkeys}
            @keyup=${this._stopTextInputHotkeys}
          />`
        : nothing}
    </div>`;
  }
}

export const BrushHeadingBlockInteraction =
  GfxViewInteractionExtension<BrushHeadingCanvasBlockComponent>(
    BrushHeadingBlockSchema.model.flavour,
    {
      resizeConstraint: {
        allowedHandlers: [
          'top-left',
          'top-right',
          'left',
          'right',
          'bottom-left',
          'bottom-right',
        ],
        lockRatio: ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
        minWidth: 220,
        minHeight: 58,
      },
      handleResize: () => ({
        onResizeStart(context) {
          context.default(context);
        },
        onResizeMove(context) {
          context.default(context);
        },
        onResizeEnd(context) {
          context.default(context);
        },
      }),
      handleSelection: ({ gfx, view, model }) => ({
        onSelect(context) {
          const { selected, multiSelect } = context;
          const alreadySelected = gfx.selection.has(model.id);
          if (!multiSelect && selected && alreadySelected) {
            if (model.isLocked() || gfx.selection.editing) return;
            gfx.selection.set({ elements: [model.id], editing: true });
            void view.updateComplete.then(() => {
              if (!view.isConnected) return;
              view.shadowRoot?.querySelector('input')?.focus();
            });
            return;
          }
          context.default(context);
        },
      }),
    }
  );

declare global {
  interface HTMLElementTagNameMap {
    [AFFINE_BRUSH_HEADING_CANVAS]: BrushHeadingCanvasBlockComponent;
  }
}
